import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Plus } from 'lucide-react'
import { PageHeader } from '@/components/PageHeader'
import { columns } from '@/pages/userRoles/columns'
import IfAnyPermission from '@/components/IfAnyPermission'
import { AllowedAction } from '@/data/allowedActions'
import { roleWritePermission } from '@/pages/userRoles/roleActionPermission'
import { DataTable } from '@/pages/userRoles/data-table'
import { useUserRoles } from '@/hooks/useUserRoles'
import { Skeleton } from '@/components/ui/skeleton'
import { ClinicGroupUserRoleSettingDialog } from './ClinicGroupUserRoleSettingDialog'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import { useViewMode } from '@/stores/viewMode'
import { useClinicGroup } from '@/hooks/useClinicGroups'

export default function ClinicGroupUserRolesPage() {
  const [page, setPage] = useState(1)
  const [pageSize] = useState(10)
  const [search, setSearch] = useState('')
  const [showAddDialog, setShowAddDialog] = useState(false)
  const { id: clinicGroupId } = useViewMode()
  const { clinicGroup } = useClinicGroup(clinicGroupId)

  const { userRoles, pagination, isLoading, isError, mutate } = useUserRoles({
    page,
    pageSize,
    search,
    clinicGroupId: clinicGroupId ? clinicGroupId : undefined,
    scopeType: 'clinic_group',
  })

  if (isError) {
    return (
      <div>
        <div className="text-center text-destructive">
          載入角色列表失敗。請稍後再試。
        </div>
      </div>
    )
  }

  if (!clinicGroupId) {
    return <div className="text-center text-destructive">未選擇診所集團</div>
  }

  if (isLoading) {
    return (
      <div>
        <Skeleton className="h-8 w-[200px] mb-4" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    )
  }

  // Generate page numbers for pagination
  const generatePaginationItems = () => {
    if (!pagination) return []

    const items = []
    const { page: currentPage, totalPages } = pagination

    // Always show first page
    items.push(1)

    if (currentPage > 3) {
      items.push('ellipsis')
    }

    // Show pages around current page
    for (
      let i = Math.max(2, currentPage - 1);
      i <= Math.min(totalPages - 1, currentPage + 1);
      i++
    ) {
      items.push(i)
    }

    if (currentPage < totalPages - 2) {
      items.push('ellipsis')
    }

    // Always show last page
    if (totalPages > 1) {
      items.push(totalPages)
    }

    return items
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="集團角色管理"
        description={`${clinicGroup?.group_name ?? '診所集團'} - 管理集團層級的用戶角色和權限`}
        level="section"
      >
        <IfAnyPermission
          permissions={roleWritePermission('clinic_group', AllowedAction.C)}
          requireExplicitForAdmin
        >
          <Button onClick={() => setShowAddDialog(true)}>
            <Plus className="mr-2 h-4 w-4" />
            新增角色
          </Button>
        </IfAnyPermission>
      </PageHeader>

      <ClinicGroupUserRoleSettingDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        onSuccess={() => {
          mutate()
          setShowAddDialog(false)
        }}
      />

      <Card>
        <CardContent className="p-6 space-y-4">
          <DataTable
            columns={columns}
            data={userRoles}
            onSearch={setSearch}
            meta={{
              reloadData: () => {
                mutate()
              },
            }}
          />

          {pagination && (
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  {page <= 1 ? (
                    <PaginationPrevious className="pointer-events-none opacity-50" />
                  ) : (
                    <PaginationPrevious onClick={() => setPage(page - 1)} />
                  )}
                </PaginationItem>

                {generatePaginationItems().map((item, index) =>
                  item === 'ellipsis' ? (
                    <PaginationItem key={`ellipsis-${index}`}>
                      <PaginationEllipsis />
                    </PaginationItem>
                  ) : (
                    <PaginationItem key={item}>
                      <PaginationLink
                        onClick={() => setPage(item as number)}
                        isActive={pagination.page === item}
                      >
                        {item}
                      </PaginationLink>
                    </PaginationItem>
                  ),
                )}

                <PaginationItem>
                  {page >= (pagination?.totalPages || 1) ? (
                    <PaginationNext className="pointer-events-none opacity-50" />
                  ) : (
                    <PaginationNext onClick={() => setPage(page + 1)} />
                  )}
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
